import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Loader from "@/components/Loader";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Collections from "@/pages/Collections";
import { Toaster } from "@/components/ui/sonner";
import { BRAND } from "@/lib/data";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

/**
 * On route change: scroll to top unless the URL has a hash — in which case
 * scroll to the matching element (used by nav links pointing to /#consultation).
 */
function ScrollManager() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            // Give the page a beat to render before scrolling
            const id = hash.slice(1).split("?")[0];
            setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                else window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
            }, 60);
        } else {
            window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
        }
    }, [pathname, hash]);
    return null;
}

function AppShell() {
    return (
        <>
            <ScrollManager />
            <Loader minMs={800} />
            <Navbar />

            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="*" element={<Home />} />
            </Routes>

            <Footer />

            {/* Floating WhatsApp Button */}
            <a
                href={buildWhatsAppUrl("Hello The Treasure Threads, I'd like to enquire about your collection.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="floating-whatsapp-btn"
                className="fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full bg-[#111111] text-white flex items-center justify-center shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] hover:bg-[#D4AF37] hover:text-[#111111] transition-all duration-500 group"
                aria-label={`WhatsApp ${BRAND.phone}`}
            >
                <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" strokeWidth={1.4} />
            </a>

            <Toaster
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#111111",
                        color: "#F8F8F6",
                        border: "1px solid #333333",
                        borderRadius: 0,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.85rem",
                        letterSpacing: "0.02em",
                    },
                }}
            />
        </>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AppShell />
            </BrowserRouter>
        </div>
    );
}
